import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useSettings } from '@/lib/settings';
import { cn } from '@/lib/utils';

const NAV = [
    { href: '/', label: 'Početna' },
    { href: '/usluge', label: 'Usluge' },
    { href: '/reference', label: 'Reference' },
    { href: '/blog', label: 'Blog' },
    { href: '/o-nama', label: 'O nama' },
    { href: '/kontakt', label: 'Kontakt' },
];

export default function Header() {
    const { settings } = useSettings();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const phoneLink = settings?.phone_link || 'tel:+385977094158';
    const phoneDisplay = settings?.phone || '+385 97 709 4158';
    const logo = settings?.logo_url;
    // Logo height: admin-configurable (clamped 24-96 server-side). Mobile = 75% of desktop.
    const logoH = Math.max(24, Math.min(96, parseInt(settings?.logo_height, 10) || 40));
    const logoHMobile = Math.round(logoH * 0.75);

    return (
        <header
            data-testid="site-header"
            className={cn(
                'sticky top-0 z-50 transition-all duration-300',
                'bg-white/85 supports-[backdrop-filter]:bg-white/70 backdrop-blur',
                'border-b border-border/70',
                scrolled && 'shadow-[var(--sk-shadow-sm)]'
            )}
        >
            <div
                className={cn(
                    'mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300',
                    scrolled ? 'h-14 sm:h-16' : 'h-16 sm:h-20'
                )}
            >
                {/* Logo */}
                <Link
                    to="/"
                    className={cn('flex items-center transition-transform duration-300', scrolled && 'scale-[0.92]')}
                    aria-label="Škobić Keramika – početna"
                    data-testid="site-header-logo-link"
                >
                    {logo ? (
                        <img
                            src={logo}
                            alt="Škobić Keramika"
                            className="sk-logo"
                            style={{
                                '--logo-h-mobile': `${logoHMobile}px`,
                                '--logo-h-desktop': `${logoH}px`,
                            }}
                            loading="eager"
                            decoding="sync"
                        />
                    ) : (
                        <span className="font-display text-xl font-semibold tracking-tight">
                            Škobić Keramika
                        </span>
                    )}
                </Link>

                {/* Desktop nav */}
                <nav
                    className="hidden lg:flex items-center gap-8"
                    aria-label="Glavna navigacija"
                    data-testid="site-header-nav"
                >
                    {NAV.map((item) => (
                        <Link
                            key={item.href}
                            to={item.href}
                            className={cn(
                                'relative text-sm font-medium transition-colors',
                                location.pathname === item.href
                                    ? 'text-foreground'
                                    : 'text-foreground/70 hover:text-foreground'
                            )}
                            data-testid={`site-header-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            {item.label}
                            <span
                                className={cn(
                                    'absolute -bottom-1 left-0 h-px w-0 bg-[color:var(--sk-accent)] transition-all duration-300',
                                    location.pathname === item.href ? 'w-full' : 'group-hover:w-full'
                                )}
                            />
                        </Link>
                    ))}
                </nav>

                {/* Right: CTA + Mobile menu */}
                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        size={scrolled ? 'sm' : 'default'}
                        className="hidden sm:inline-flex bg-[color:var(--sk-text)] text-white hover:bg-black rounded-xl"
                        data-testid="site-header-call-cta"
                    >
                        <a href={phoneLink} aria-label={`Pozovi ${phoneDisplay}`}>
                            <Phone className="mr-2 h-4 w-4" />
                            <span className="hidden md:inline">Pozovi</span>
                            <span className="md:hidden">Pozovi</span>
                        </a>
                    </Button>

                    {/* Mobile call icon */}
                    <Button
                        asChild
                        size="icon"
                        className="sm:hidden bg-[color:var(--sk-text)] text-white hover:bg-black"
                        data-testid="site-header-call-icon"
                    >
                        <a href={phoneLink} aria-label="Pozovi">
                            <Phone className="h-4 w-4" />
                        </a>
                    </Button>

                    {/* Mobile hamburger */}
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                aria-label="Otvori meni"
                                data-testid="site-header-mobile-menu-button"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[300px] sm:w-[360px] bg-white">
                            <SheetTitle className="sr-only">Glavna navigacija</SheetTitle>
                            <div className="mt-4 mb-8 flex items-center justify-between">
                                {logo && (
                                    <img
                                        src={logo}
                                        alt="Škobić Keramika"
                                        className="h-8 w-auto"
                                    />
                                )}
                            </div>
                            <nav className="flex flex-col gap-1">
                                {NAV.map((item) => (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        className={cn(
                                            'rounded-lg px-3 py-3 text-base font-medium transition-colors',
                                            location.pathname === item.href
                                                ? 'bg-[color:var(--sk-surface-2)] text-foreground'
                                                : 'text-foreground/80 hover:bg-[color:var(--sk-surface-2)]/60'
                                        )}
                                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </nav>
                            <div className="mt-8 pt-6 border-t border-border">
                                <Button
                                    asChild
                                    className="w-full bg-[color:var(--sk-text)] text-white hover:bg-black"
                                    data-testid="mobile-call-cta"
                                >
                                    <a href={phoneLink}>
                                        <Phone className="mr-2 h-4 w-4" />
                                        {phoneDisplay}
                                    </a>
                                </Button>
                                <p className="sk-kicker mt-6">Lokacija</p>
                                <p className="mt-1 text-sm text-foreground/80">
                                    {settings?.address || 'Poreč, Istra'}
                                </p>
                                {settings?.email && (
                                    <>
                                        <p className="sk-kicker mt-4">E-mail</p>
                                        <a
                                            href={`mailto:${settings.email}`}
                                            className="mt-1 text-sm text-foreground/80 underline underline-offset-4"
                                        >
                                            {settings.email}
                                        </a>
                                    </>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
